import {
  Briefcase,
  CalendarPlus,
  Car,
  CarFront,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FileText,
  Megaphone,
  Package,
  Settings,
  Users,
  Users2,
  Wallet,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { NavLink } from '@/components/NavLink'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useAbility } from '@/contexts/AbilityContext'
import { useIsMobile } from '@/hooks/useMobile'
import type { Subjects } from '@/libs/ability'
import { APP_ROUTES } from '@/libs/constants'
import { cn } from '@/libs/utils'
interface AppSidebarProps {
  mobileOpen?: boolean
  onMobileOpenChange?: (open: boolean) => void
}

export function AppSidebar({
  mobileOpen: controlledMobileOpen,
  onMobileOpenChange,
}: AppSidebarProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState(false)
  const [userCollapsed, setUserCollapsed] = useState(false)
  const [hovered, setHovered] = useState(false)
  const [internalMobileOpen, setInternalMobileOpen] = useState(false)
  const isMobile = useIsMobile()

  const mobileOpen = controlledMobileOpen ?? internalMobileOpen
  const setMobileOpen = onMobileOpenChange ?? setInternalMobileOpen

  const isSettingsPage = location.pathname.startsWith(APP_ROUTES.settings.base)

  // Auto-collapse on settings page
  useEffect(() => {
    if (isSettingsPage) {
      setCollapsed(true)
    } else {
      setCollapsed(userCollapsed)
    }
  }, [isSettingsPage, userCollapsed])

  useEffect(() => {
    if (!isMobile) {
      setMobileOpen(false)
    }
  }, [isMobile, setMobileOpen])
  const ability = useAbility()
  interface NavItems {
    title: string
    url: string
    icon: React.ElementType
    tooltipKey: string
    subject: Subjects | string
    order: number
  }
  // `order` is configurable in future (e.g. via settings/user prefs).
  // Items are rendered sorted by ascending `order`.

  const allNavItems: NavItems[] = [
    {
      title: t('nav.scheduler'),
      url: APP_ROUTES.booking.base,
      icon: CalendarPlus,
      tooltipKey: 'tooltips.nav.booking',
      subject: 'Job',
      order: 1,
    },
    {
      title: t('nav.customers'),
      url: APP_ROUTES.customer.base,
      icon: Users,
      tooltipKey: 'tooltips.nav.customers',
      subject: 'Customer',
      order: 2,
    },
    {
      title: t('nav.vehicles'),
      url: APP_ROUTES.vehicle.base,
      icon: Car,
      tooltipKey: 'tooltips.nav.vehicles',
      subject: 'Vehicle',
      order: 3,
    },
    {
      title: t('nav.jobs'),
      url: APP_ROUTES.job.base,
      icon: ClipboardList,
      tooltipKey: 'tooltips.nav.jobs',
      subject: 'Job',
      order: 4,
    },
    {
      title: t('nav.myWorkspace'),
      url: '/my-workspace',
      icon: Briefcase,
      tooltipKey: 'tooltips.nav.myWorkspace',
      subject: 'WorkLog',
      order: 5,
    },
    {
      title: t('nav.teamOverview'),
      url: '/team-overview',
      icon: Users2,
      tooltipKey: 'tooltips.nav.teamOverview',
      subject: 'WorkLog',
      order: 6,
    },
    {
      title: t('nav.billing'),
      url: '/invoicing',
      icon: FileText,
      tooltipKey: 'tooltips.nav.billing',
      subject: 'Billing',
      order: 7,
    },
    {
      title: t('nav.economy'),
      url: '/economy',
      icon: Wallet,
      tooltipKey: 'tooltips.nav.economy',
      subject: 'Billing',
      order: 8,
    },
    {
      title: t('nav.products'),
      url: APP_ROUTES.product.base,
      icon: Package,
      tooltipKey: 'tooltips.nav.products',
      subject: 'Product',
      order: 9,
    },
    {
      title: t('nav.rentalFleet'),
      url: APP_ROUTES.rentCar.base,
      icon: CarFront,
      tooltipKey: 'tooltips.nav.rentalFleet',
      subject: 'LoanCar',
      order: 10,
    },
    {
      title: t('nav.campaigns'),
      url: '/campaigns',
      icon: Megaphone,
      tooltipKey: 'tooltips.nav.campaigns',
      subject: 'Campaign',
      order: 11,
    },
    {
      title: t('nav.settings'),
      url: APP_ROUTES.settings.base,
      icon: Settings,
      tooltipKey: 'tooltips.nav.settings',
      subject: 'Settings',
      order: 12,
    },
  ].sort((a, b) => a.order - b.order)
  const navItems = allNavItems.filter((item) => ability.can('read', item.subject as Subjects))
  const showExpanded = !collapsed || hovered

  const SidebarContent = ({ isSheet = false }: { isSheet?: boolean }) => {
    const expanded = showExpanded || isSheet
    return (
      <>
        {/* Header */}
        <header
          className="h-14 md:h-16 px-2 md:px-3 flex items-center justify-between bg-sidebar shrink-0"
          data-tour="sidebar-logo"
        >
          <button
            onClick={() => navigate(APP_ROUTES.home)}
            className={cn(
              'flex items-center gap-2 rounded-lg transition-all duration-200 hover:opacity-90 min-w-0 overflow-hidden bg-white shadow-sm',
              expanded ? 'flex-1 justify-center px-2 py-1.5' : 'w-10 h-10 justify-center p-1',
            )}
            aria-label={t('nav.homeAriaLabel')}
          >
            {expanded ? (
              <img
                src="/logo_.png"
                alt={t('common.appName')}
                className="h-8 w-auto max-w-full object-contain"
              />
            ) : (
              <div
                aria-hidden="true"
                className="w-8 h-8"
                style={{
                  backgroundImage: `url(/logo_.png)`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'left center',
                  // Logo is 844x180; emblem ~ first 180px. Scale so 32px height -> 32 * 844/180 ≈ 150px wide bg.
                  backgroundSize: 'auto 100%',
                }}
              />
            )}
          </button>
        </header>

        {/* Navigation */}
        <nav
          className="flex-1 p-1.5 md:p-2 space-y-0.5 overflow-y-auto"
          aria-label={t('nav.primaryNavigation')}
          data-tour="sidebar-nav"
        >
          {navItems.map((item) => {
            const isActive = location.pathname.startsWith(item.url)
            return !expanded ? (
              <Tooltip key={item.url}>
                <TooltipTrigger asChild>
                  <NavLink
                    to={item.url}
                    end={item.url === '/'}
                    onClick={() => isSheet && setMobileOpen(false)}
                    className={cn(
                      'flex items-center gap-2.5 px-2.5 py-1.5 md:py-2 rounded-md transition-all duration-200 text-[13px]',
                      'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent',
                      'justify-center',
                      isActive && 'bg-sidebar-accent text-sidebar-primary font-medium',
                    )}
                    aria-label={item.title}
                  >
                    <item.icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                  </NavLink>
                </TooltipTrigger>
                <TooltipContent side="right">{t(item.tooltipKey)}</TooltipContent>
              </Tooltip>
            ) : (
              <NavLink
                key={item.url}
                to={item.url}
                end={item.url === '/'}
                onClick={() => isSheet && setMobileOpen(false)}
                className={cn(
                  'flex items-center gap-2.5 px-2.5 py-1.5 md:py-2 rounded-md transition-all duration-200 text-[13px]',
                  'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent',
                  isActive && 'bg-sidebar-accent text-sidebar-primary font-medium',
                )}
              >
                <item.icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                <span className="whitespace-nowrap">{item.title}</span>
              </NavLink>
            )
          })}
        </nav>

        {/* Collapse toggle */}
        {!isSheet && (
          <button
            onClick={() => {
              const next = !collapsed
              setCollapsed(next)
              setUserCollapsed(next)
            }}
            className="flex items-center justify-center h-12 border-t border-sidebar-border text-sidebar-muted hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors"
            aria-label={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        )}
      </>
    )
  }

  // Mobile sidebar (Sheet)
  if (isMobile) {
    return (
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          className="w-[280px] p-0 bg-sidebar text-sidebar-foreground border-sidebar-border"
        >
          <SidebarContent isSheet />
        </SheetContent>
      </Sheet>
    )
  }

  // Desktop sidebar
  return (
    <TooltipProvider delayDuration={200}>
      {/* Spacer div to reserve layout space - expands on hover to push content */}
      <div
        className={cn(
          'h-screen shrink-0 hidden md:block transition-all duration-300',
          collapsed && !hovered ? 'w-14' : 'w-52 lg:w-56',
        )}
      />
      {/* Actual sidebar - fixed positioning for smooth transitions */}
      <aside
        className={cn(
          'h-screen bg-sidebar text-sidebar-foreground flex flex-col transition-all duration-300 fixed top-0 left-0 z-[60] hidden md:flex',
          collapsed && !hovered ? 'w-14' : 'w-52 lg:w-56',
        )}
        onMouseEnter={() => {
          if (collapsed) setHovered(true)
        }}
        onMouseLeave={() => {
          if (collapsed) setHovered(false)
        }}
        aria-label={t('nav.mainNavigation')}
      >
        <SidebarContent />
      </aside>
    </TooltipProvider>
  )
}
